import { useKpisPerimeter, type FiltersTreeType } from '#composables/kpisPerimeter';
import { filterToTreeViewItemWithDefaultValue } from '#domain/core/filter/filter.mapper';
import { findStoresRegion } from '#domain/core/filter/filter.query';
import { isNielsenInterval, PERIOD_TO_LOOKER_PERIOD, type ProductTaxonomyType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';

interface UseStoresRegionDataProps {
  debouncedSearch: Readonly<Ref<string>>;
  enabled: Ref<boolean>;
  checkedItemsState: Ref<FiltersTreeType>;
  taxonomy: Ref<ProductTaxonomyType>;
}

export function useStoresRegionData({
  debouncedSearch,
  enabled,
  checkedItemsState,
  taxonomy
}: UseStoresRegionDataProps) {
  const {
    period: { selectedPeriod },
    storePerimeter: { storePerimeter }
  } = useKpisPerimeter();

  const currentStoreRegionFilters = computed(() => {
    return Object.fromEntries(
      Object.entries(checkedItemsState.value.storesRegionFilters).filter(([, state]) => state.state === 'checked')
    );
  });

  const {
    data: storesRegionData,
    isLoading,
    isFetching
  } = useQuery({
    enabled,
    queryKey: [findStoresRegion.name, debouncedSearch, taxonomy, selectedPeriod, storePerimeter],
    queryFn: async () => {
      const periodField = PERIOD_TO_LOOKER_PERIOD[selectedPeriod.value.period];
      const unrefTaxonomy = unref(taxonomy);

      if (
        unrefTaxonomy !== 'nielsen' &&
        periodField !== 'period_code' &&
        !isNielsenInterval(selectedPeriod.value.periodInterval) &&
        storePerimeter.value === 'likeForLike'
      ) {
        return findStoresRegion({
          search: debouncedSearch.value,
          orNodeIdIn: debouncedSearch.value ? Object.keys(currentStoreRegionFilters.value) : undefined,
          limit: 100,
          taxonomy: unrefTaxonomy,
          storePerimeter: unref(storePerimeter),
          period: {
            field: periodField,
            value: selectedPeriod.value.periodInterval
          }
        });
      }

      return findStoresRegion({
        search: debouncedSearch.value,
        orNodeIdIn: debouncedSearch.value ? Object.keys(currentStoreRegionFilters.value) : undefined,
        limit: 100,
        taxonomy: unrefTaxonomy,
        storePerimeter: 'allStores'
      });
    },
    select: (result) => {
      if (result.status === 'error') {
        return undefined;
      }

      return result.data;
    }
  });

  const itemsStoresRegion = computed(() => {
    if (!storesRegionData.value) {
      return undefined;
    }

    return filterToTreeViewItemWithDefaultValue({
      filter: storesRegionData.value,
      defaultFilters: storesRegionData.value,
      selectedFilters: currentStoreRegionFilters.value
    });
  });

  const isLoadingStoresRegion = computed(() => {
    return isLoading.value && isFetching.value;
  });

  return {
    isLoadingStoresRegion,
    itemsStoresRegion
  };
}
